import { getMainLanguage, SupportedLocale } from "@/i18n";
import { DimensionsFormatting } from "@/lib/compute-field/shared/dimensions";
import { resolveEmbedThumbnailUrl } from "@/lib/media/embed";
import {
  getDefaultImage,
  selectAttachmentForPreview,
  TypeDefaultImage,
} from "@/lib/image";
import {
  moneyAmountToMajorUnits,
  StoredMoneyAmount,
} from "@/lib/shared-types";
import { Attachment, Dimensions } from "@/lib/types";
import { parseJsonOrUndefined, sanitizeRichTextHtml } from "@/lib/utils";
import { ImageFullscreen } from "../modal/imageFullscreen";
import { Badge } from "../ui/badge";

export const TextCell = ({ value }: { value: string }) => {
  if (!value) {
    return <div className="flex items-center gap-2 text-wrap"></div>;
  }
  return (
    <div className="flex items-center gap-2 text-wrap font-['Raleway']">
      <p className="text-[14px] font-[500]">{value}</p>
    </div>
  );
};

export const ImageTextCell = ({
  value,
  attachments,
  domain,
}: {
  value: string;
  attachments: Attachment[];
  domain: TypeDefaultImage;
}) => {
  const image = getDefaultImage(attachments, domain, "publicUrl");
  return (
    <div className="flex items-center gap-3 text-wrap">
      {image && (
        <img
          src={image}
          alt="oeuvre"
          className="size-0 aspect-square object-contain"
        />
      )}
      <p className="text-[14px] font-[500] font-['Raleway']">{value}</p>
    </div>
  );
};

export const ImageCell = ({
  attachments,
  domain,
}: {
  value: string;
  attachments: Attachment[];
  domain: TypeDefaultImage;
}) => {
  const previewAttachment = selectAttachmentForPreview(attachments, domain);
  const image =
    previewAttachment && previewAttachment.isGenericImage !== true
      ? previewAttachment.kind === "embed"
        ? resolveEmbedThumbnailUrl(
            previewAttachment.embedProvider,
            previewAttachment.embedVideoId,
            previewAttachment.embedUrl,
          ) ?? null
        : previewAttachment.kind === "video"
          ? previewAttachment.posterPublicUrl ||
            previewAttachment.mediumPublicUrl ||
            previewAttachment.publicUrl ||
            previewAttachment.largePublicUrl ||
            null
          : previewAttachment.mediumPublicUrl ||
            previewAttachment.publicUrl ||
            previewAttachment.largePublicUrl ||
            null
      : null;
  const canRender =
    !!image ||
    previewAttachment?.isGenericImage === true ||
    (previewAttachment?.kind === "video" && !!previewAttachment.videoPublicUrl) ||
    previewAttachment?.kind === "embed";
  if (!canRender) {
    return <div className="flex items-center gap-3 text-wrap"></div>;
  }
  return (
    <ImageFullscreen
      media={{
        attachments,
        type: domain,
        alt: "thumbnail",
        width: 70,
        height: 70,
        className: "size-[70px] aspect-square object-contain",
      }}
    />
  );
};

export const BadgeCell = ({
  value,
  variant,
}: {
  value: string;
  variant: "outlined" | "contained" | "published" | "draft";
}) => {
  if (!value) {
    return <div className="flex items-center gap-2 text-wrap"></div>;
  }
  return (
    <div className="flex items-center gap-2 text-wrap">
      <Badge variant={variant}>{value}</Badge>
    </div>
  );
};

export const HtmlCell = ({ value }: { value: string }) => {
  if (!value) {
    return <div className="flex items-center gap-2 text-wrap"></div>;
  }

  const sanitizedHtml = sanitizeRichTextHtml(value);

  return (
    <div className="flex items-center gap-2 text-wrap">
      <div
        className="text-[14px] font-[500] font-['Raleway']"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </div>
  );
};

export const CurrencyCell = ({ value }: { value: unknown }) => {
  if (!value) {
    return <div className="flex items-center gap-2 text-wrap">-</div>;
  }

  const parsedValue = parseJsonOrUndefined<StoredMoneyAmount>(value);
  const formattedValue =
    parsedValue &&
    typeof parsedValue === "object" &&
    !Array.isArray(parsedValue)
      ? parsedValue
      : undefined;

  if (!formattedValue) {
    return <div className="flex items-center gap-2 text-wrap">-</div>;
  }

  const displayAmount = moneyAmountToMajorUnits(formattedValue.amount);

  return (
    <div className="flex items-center gap-2 text-wrap">
      {formatNumber(displayAmount, formattedValue.currency)}
    </div>
  );
};

const formatNumber = (amount?: number, currency?: string) => {
  if (amount == null) {
    return "-";
  }
  if (!currency) {
    return amount.toFixed(2);
  }
  return new Intl.NumberFormat(getMainLanguage() as SupportedLocale, {
    style: "currency",
    currency: currency.toLowerCase(),
  }).format(amount);
};

/**
 * Renders dimensions for table cells. Resolves raw and unknown dimensions from
 * the row using naming convention: `{dimensionKey}Raw` and `{dimensionKey}Inconnues`.
 */
export const DimensionsCell = ({
  value,
  row,
  dimensionKey,
}: {
  value: unknown;
  row: Record<string, unknown>;
  dimensionKey: string;
}) => {
  if (!value) {
    return <div className="flex items-center gap-2 text-wrap">-</div>;
  }
  const parsed = parseJsonOrUndefined(value) as Dimensions | undefined;
  const rawKey = `${dimensionKey}Raw`;
  const inconnuesKey = `${dimensionKey}Inconnues`;
  const rawDimensions =
    rawKey in row && row[rawKey] != null && String(row[rawKey]).trim()
      ? String(row[rawKey])
      : undefined;
  const unknownDimensions =
    inconnuesKey in row ? Boolean(row[inconnuesKey]) : undefined;

  return (
    <DimensionsFormatting
      input={{
        ...parsed,
        ...(rawDimensions !== undefined && { rawDimensions }),
        ...(unknownDimensions !== undefined && { unknownDimensions }),
      }}
      lineBreak={false}
      language={getMainLanguage() as SupportedLocale}
    />
  );
};
